/**
 * Verlauf seitenweise je Ansicht (50 je Seite, Cursor des Servers) und der ZIP-Export mit dem
 * aktuellen Filter ohne Ansicht und Cursor. Der Export trägt den Dateinamen des Servers.
 */
import { useEffect, useRef, useState } from 'react';
import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import type { DynamicPricingHistoryQuery, DynamicPricingHistoryResponse } from '@/shared/api';
import { useApi } from '@/shared/api';

export const HISTORY_PAGE_SIZE = 50;

export function useDynamicPricingHistory(query: DynamicPricingHistoryQuery, enabled = true) {
  const api = useApi();
  const view = query.view ?? 'decisions';
  const cursorKey = `${view.slice(0, -1)}Cursor` as
    | 'decisionCursor'
    | 'publicationCursor'
    | 'exposureCursor';
  const history = useInfiniteQuery({
    queryKey: ['dynamic-pricing-history', query],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.dynamicPricingHistory.get({
        ...query,
        [cursorKey]: pageParam,
        pageSize: HISTORY_PAGE_SIZE,
      }),
    getNextPageParam: (lastPage) => lastPage[view].nextCursor ?? undefined,
    enabled,
  });
  const download = useMutation({
    mutationFn: () => {
      const {
        view: _,
        decisionCursor: _decision,
        publicationCursor: _publication,
        exposureCursor: _exposure,
        ...filter
      } = query;
      return api.dynamicPricingHistory.export(filter);
    },
    retry: false,
    // Offline sofort scheitern („Keine Verbindung“), statt endlos „läuft“ zu zeigen.
    networkMode: 'always',
    onSuccess: ({ blob, fileName }) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName ?? 'dynamic-pricing-history.zip';
      link.click();
      // Sofort freigegeben, bricht mancher Browser den Download ab.
      setTimeout(() => URL.revokeObjectURL(url), 1_000);
    },
  });
  const stalePages = useStalePages(history, JSON.stringify(query));
  const data = history.data ? mergePages(view, history.data.pages) : undefined;
  return {
    ...history,
    stalePages,
    data,
    hasMore: history.hasNextPage,
    isLoadingMore: history.isFetchingNextPage,
    loadMore: history.fetchNextPage,
    exportHistory: () => download.mutate(),
    isExporting: download.isPending,
    exportError: download.error,
    resetExport: download.reset,
  };
}

/**
 * Scheitert das Neuladen der geladenen Seiten, bleiben sie veraltet – auch wenn danach „Weitere 50“
 * gelingt und der Fehler aus dem Abfragezustand verschwindet. Gemerkt je Filter: wer
 * kurz wegwechselt und aus dem Zwischenspeicher zurückkommt, sieht dieselben alten Seiten und
 * denselben Hinweis. Erst ein gelungenes Neuladen dieses Filters macht ihn wieder frisch.
 */
export function useStalePages(
  history: { isRefetching: boolean; isRefetchError: boolean; isSuccess: boolean },
  filter: string,
) {
  const [stale, setStale] = useState<ReadonlySet<string>>(() => new Set());
  const wasRefetching = useRef(false);
  const shownFilter = useRef(filter);
  useEffect(() => {
    const sameFilter = shownFilter.current === filter;
    shownFilter.current = filter;
    const mark = (on: boolean) =>
      setStale((current) => {
        if (current.has(filter) === on) return current;
        const next = new Set(current);
        if (on) next.add(filter);
        else next.delete(filter);
        return next;
      });
    if (history.isRefetchError) mark(true);
    else if (sameFilter && wasRefetching.current && !history.isRefetching && history.isSuccess) {
      mark(false);
    }
    wasRefetching.current = history.isRefetching;
  }, [filter, history.isRefetching, history.isRefetchError, history.isSuccess]);
  return stale.has(filter);
}

function mergePages(
  view: NonNullable<DynamicPricingHistoryQuery['view']>,
  pages: DynamicPricingHistoryResponse[],
): DynamicPricingHistoryResponse {
  return {
    decisions: {
      items: view === 'decisions' ? pages.flatMap((page) => page.decisions.items) : [],
      nextCursor: null,
    },
    publications: {
      items: view === 'publications' ? pages.flatMap((page) => page.publications.items) : [],
      nextCursor: null,
    },
    exposures: {
      items: view === 'exposures' ? pages.flatMap((page) => page.exposures.items) : [],
      nextCursor: null,
    },
  };
}
